import { useReducer } from 'react';

import { useDispatch } from 'react-redux';
import { doClearCreateOrder, doClearGetQuote, doClearGetQuoteChart } from 'src/actions';
import { OrderActionDto, OrderDurationDto, OrderTypeDto } from 'src/dtos';
import { SinglePageModal } from 'src/lib';
import { Position } from 'src/models';

import { SecurityHoldingTradeDetailsStep } from './SecurityHoldingTradeDetailsStep';
import { SecurityHoldingTradeFailedStep } from './SecurityHoldingTradeFailedStep';
import { SecurityHoldingTradeMarketPriceStep } from './SecurityHoldingTradeMarketPriceStep';
import { SecurityHoldingTradeOrderFactorStep } from './SecurityHoldingTradeOrderFactorStep';
import { SecurityHoldingTradeOrderStep } from './SecurityHoldingTradeOrderStep';
import { SecurityHoldingTradeReviewStep } from './SecurityHoldingTradeReviewStep';
import { SecurityHoldingTradeServiceFeeStep } from './SecurityHoldingTradeServiceFeeStep';
import { SecurityHoldingTradeSucceededStep } from './SecurityHoldingTradeSucceededStep';

enum SecurityHoldingTradeStep {
  Details = 'Details',
  Order = 'Order',
  Review = 'Review',
  Succeeded = 'Succeeded',
  Failed = 'Failed',
  MarketPrice = 'MarketPrice',
  ServiceFee = 'ServiceFee',
  OrderFactors = 'OrderFactors',
}

export interface OrderReducerState {
  step: SecurityHoldingTradeStep;
  backStep?: SecurityHoldingTradeStep;
  duration?: OrderDurationDto;
  action?: OrderActionDto;
  type?: OrderTypeDto;
  price?: number;
  stop?: number;
  quantity?: number;
  symbol?: string;
  estimatedTotal?: number;
}
const initialOrderReducerState: OrderReducerState = {
  step: SecurityHoldingTradeStep.Details,
  type: OrderTypeDto.Market,
  duration: OrderDurationDto.Day,
};

export type OrderReducerAction = { type: 'UPDATE'; payload: Partial<OrderReducerState> } | { type: 'RESET' };

const orderReducer = (state: OrderReducerState, action: OrderReducerAction) => {
  switch (action.type) {
    case 'UPDATE':
      return {
        ...state,
        ...action.payload,
      };
    case 'RESET':
      return initialOrderReducerState;
    default:
      return state;
  }
};

interface TradeSecurityHoldingModalProps {
  open: boolean;
  position: Position;
  onClose: () => void;
}

export const TradeSecurityHoldingModal = ({ open, onClose, position }: TradeSecurityHoldingModalProps) => {
  const [orderState, orderDispatch] = useReducer(orderReducer, initialOrderReducerState);
  const dispatch = useDispatch();

  const _onClose = () => {
    onClose();
    orderDispatch({ type: 'RESET' });
    dispatch(doClearCreateOrder());
    dispatch(doClearGetQuote());
    dispatch(doClearGetQuoteChart());
  };

  const renderModal = () => {
    if (orderState.step === SecurityHoldingTradeStep.Order) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeOrderStep
            action={orderState.action}
            initialValues={orderState}
            quote={{
              id: position.symbol,
              symbol: position.symbol,
              change: position.change,
              description: position.description,
              last: position.value,
            }}
            quantity={position.quantity}
            onClose={_onClose}
            onBack={() =>
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.Details, backStep: undefined },
              })
            }
            onReview={values => {
              orderDispatch({
                type: 'UPDATE',
                payload: {
                  type: values.type,
                  duration: values.duration,
                  quantity: values.quantity ? Number(values.quantity) : undefined,
                  price: values.price ? Number(values.price) : undefined,
                  stop: values.stop ? Number(values.stop) : undefined,
                  estimatedTotal: values.estimatedTotal ? Number(values.estimatedTotal) : undefined,
                  step: SecurityHoldingTradeStep.Review,
                  backStep: SecurityHoldingTradeStep.Review,
                },
              });
            }}
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.MarketPrice, backStep: SecurityHoldingTradeStep.Order },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.ServiceFee, backStep: SecurityHoldingTradeStep.Order },
              });
            }}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingTradeStep.Review) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeReviewStep
            quote={{
              id: position.symbol,
              symbol: position.symbol,
              change: position.change,
              description: position.description,
              last: position.value,
            }}
            quantity={position.quantity}
            order={orderState}
            onClose={_onClose}
            onBack={() =>
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.Order, backStep: SecurityHoldingTradeStep.Order },
              })
            }
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.MarketPrice, backStep: SecurityHoldingTradeStep.Review },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.ServiceFee, backStep: SecurityHoldingTradeStep.Review },
              });
            }}
            onReadMore={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.OrderFactors, backStep: SecurityHoldingTradeStep.Review },
              });
            }}
            onSuccess={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.Succeeded, backStep: undefined },
              });
            }}
            onError={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.Failed, backStep: undefined },
              });
            }}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingTradeStep.MarketPrice) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeMarketPriceStep
            onBack={() => orderDispatch({ type: 'UPDATE', payload: { step: orderState.backStep } })}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingTradeStep.ServiceFee) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeServiceFeeStep
            onBack={() => orderDispatch({ type: 'UPDATE', payload: { step: orderState.backStep } })}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingTradeStep.OrderFactors) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeOrderFactorStep
            onBack={() => {
              orderDispatch({ type: 'UPDATE', payload: { step: orderState.backStep } });
            }}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingTradeStep.Succeeded) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeSucceededStep
            quote={{
              id: position.symbol,
              symbol: position.symbol,
              change: position.change,
              description: position.description,
              last: position.value,
            }}
            quantity={position.quantity}
            onClose={_onClose}
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.MarketPrice, backStep: SecurityHoldingTradeStep.Succeeded },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.ServiceFee, backStep: SecurityHoldingTradeStep.Succeeded },
              });
            }}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingTradeStep.Failed) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeFailedStep
            quote={{
              id: position.symbol,
              symbol: position.symbol,
              change: position.change,
              description: position.description,
              last: position.value,
            }}
            quantity={position.quantity}
            order={orderState}
            onClose={_onClose}
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.MarketPrice, backStep: SecurityHoldingTradeStep.Failed },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingTradeStep.ServiceFee, backStep: SecurityHoldingTradeStep.Failed },
              });
            }}
          />
        ),
      };
    }

    return {
      width: 600,
      onClose: _onClose,
      content: (
        <SecurityHoldingTradeDetailsStep
          onClose={_onClose}
          position={position}
          onBuy={() => {
            orderDispatch({
              type: 'UPDATE',
              payload: { action: OrderActionDto.Buy, step: SecurityHoldingTradeStep.Order },
            });
          }}
          onSell={() => {
            orderDispatch({
              type: 'UPDATE',
              payload: { action: OrderActionDto.Sell, step: SecurityHoldingTradeStep.Order },
            });
          }}
        />
      ),
    };
  };

  return <SinglePageModal open={open} {...renderModal()} />;
};
